/**
 * CreditsSettings Component - Credit management in Settings page
 * Uses the same dark theme as other settings components
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Package, TrendingUp, TrendingDown, RefreshCw,
  Check, AlertTriangle, Loader2, ExternalLink, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Fetch credit summary
const useCreditSummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/credits/summary');
      setSummary(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement des crédits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { summary, loading, error, refresh: fetchSummary };
};

// Fetch credit plans
const useCreditPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get('/credits/plans');
        setPlans(response.data);
      } catch (err) {
        console.error('Failed to fetch plans:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  return { plans, loading };
};

// Fetch recent transactions
const useRecentTransactions = (limit = 5) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await api.get(`/credits/transactions?limit=${limit}`);
        setTransactions(response.data);
      } catch (err) {
        console.error('Failed to fetch transactions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [limit]);

  return { transactions, loading };
};

const CreditsSettings = () => {
  const navigate = useNavigate();
  const { summary, loading: summaryLoading, error, refresh } = useCreditSummary();
  const { plans, loading: plansLoading } = useCreditPlans();
  const { transactions, loading: txLoading } = useRecentTransactions(5);

  const totalCredits = summary?.total_credits || 0;
  const monthlyCredits = summary?.monthly_credits || 0;
  const packCredits = summary?.pack_credits || 0;

  const isLow = totalCredits > 0 && totalCredits < 50;
  const isCritical = totalCredits > 0 && totalCredits < 20;
  const isExhausted = totalCredits === 0;

  const getStatusColor = () => {
    if (isExhausted) return 'text-red-400';
    if (isCritical) return 'text-red-400';
    if (isLow) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getStatusBg = () => {
    if (isExhausted) return 'bg-red-500/10 border-red-500/30';
    if (isCritical) return 'bg-red-500/10 border-red-500/30';
    if (isLow) return 'bg-yellow-500/10 border-yellow-500/30';
    return 'bg-green-500/10 border-green-500/30';
  };

  return (
    <div className="space-y-6">
      {/* Credit Balance Card */}
      <div className="bg-brand-darker rounded-xl border border-gray-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-brand-green" />
            Solde de Crédits
          </h3>
          <button
            onClick={refresh}
            disabled={summaryLoading}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 text-gray-400 ${summaryLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {summaryLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
          </div>
        ) : error ? (
          <div className="text-red-400 text-center py-4">{error}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Total */}
            <div className={`rounded-lg border p-4 ${getStatusBg()}`}>
              <div className={`text-3xl font-bold ${getStatusColor()}`}>
                {totalCredits.toLocaleString('fr-FR')}
              </div>
              <div className="text-sm text-gray-400 mt-1">Total disponible</div>
              {(isLow || isCritical || isExhausted) && (
                <div className="flex items-center gap-1 mt-2 text-xs text-yellow-400">
                  <AlertTriangle className="w-3 h-3" />
                  {isExhausted ? 'Crédits épuisés' : 'Crédits faibles'}
                </div>
              )}
            </div>

            {/* Monthly */}
            <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
              <div className="text-2xl font-bold text-blue-400">
                {monthlyCredits.toLocaleString('fr-FR')}
              </div>
              <div className="text-sm text-gray-400 mt-1">Crédits mensuels</div>
              {summary?.monthly_credits_expire_at && (
                <div className="text-xs text-gray-500 mt-2">
                  Expire: {new Date(summary.monthly_credits_expire_at).toLocaleDateString('fr-FR')}
                </div>
              )}
            </div>

            {/* Pack */}
            <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
              <div className="text-2xl font-bold text-purple-400">
                {packCredits.toLocaleString('fr-FR')}
              </div>
              <div className="text-sm text-gray-400 mt-1">Crédits packs</div>
            </div>
          </div>
        )}

        {/* Current Plan */}
        {summary?.subscription_plan_name && (
          <div className="mt-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700 flex items-center justify-between">
            <div>
              <span className="text-gray-400 text-sm">Plan actuel: </span>
              <span className="text-white font-semibold">{summary.subscription_plan_name}</span>
              {summary.subscription_status && (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                  summary.subscription_status === 'active'
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {summary.subscription_status === 'active' ? 'Actif' :
                   summary.subscription_status === 'inactive' ? 'Inactif' :
                   summary.subscription_status === 'expired' ? 'Expiré' :
                   summary.subscription_status === 'pending' ? 'En attente' :
                   summary.subscription_status === 'cancelled' ? 'Annulé' :
                   summary.subscription_status}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Plans Overview */}
      <div className="bg-brand-darker rounded-xl border border-gray-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-brand-green" />
            Plans Disponibles
          </h3>
          <button
            onClick={() => navigate('/credits')}
            className="text-sm text-brand-green hover:text-brand-green/80 flex items-center gap-1"
          >
            Voir tout
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {plansLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 text-brand-green animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {plans.map((plan) => (
              <div
                key={plan.slug}
                className={`p-3 rounded-lg border transition-all cursor-pointer hover:border-brand-green/50 ${
                  summary?.subscription_plan_slug === plan.slug
                    ? 'bg-brand-green/10 border-brand-green'
                    : 'bg-gray-800/50 border-gray-700'
                }`}
                onClick={() => navigate('/credits')}
              >
                <div className="font-semibold text-white text-sm">{plan.name}</div>
                <div className="text-lg font-bold text-brand-green mt-1">
                  {plan.monthly_credits.toLocaleString('fr-FR')}
                  <span className="text-xs text-gray-400 font-normal"> cr/mois</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {plan.monthly_price_eur === 0 ? 'Gratuit' : `${plan.monthly_price_eur}€/mois`}
                </div>
                {summary?.subscription_plan_slug === plan.slug && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-brand-green">
                    <Check className="w-3 h-3" />
                    Actuel
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Transactions */}
      <div className="bg-brand-darker rounded-xl border border-gray-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-brand-green" />
            Transactions Récentes
          </h3>
          <button
            onClick={() => navigate('/credits')}
            className="text-sm text-brand-green hover:text-brand-green/80 flex items-center gap-1"
          >
            Historique complet
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {txLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 text-brand-green animate-spin" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-4 text-gray-400">
            Aucune transaction
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map((tx) => {
              const isPositive = ['credit_monthly_renewal', 'credit_pack_purchase', 'credit_bonus', 'credit_refund'].includes(tx.transaction_type);
              return (
                <div
                  key={tx.id}
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {isPositive ? (
                      <TrendingUp className="w-4 h-4 text-green-400" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-400" />
                    )}
                    <div>
                      <div className="text-sm text-white">
                        {tx.transaction_type.replace('credit_', '').replace(/_/g, ' ')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(tx.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </div>
                  <div className={`font-bold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                    {isPositive ? '+' : ''}{tx.credits_amount.toLocaleString('fr-FR')}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Action Button */}
      <div className="flex justify-center">
        <button
          onClick={() => navigate('/credits')}
          className="px-6 py-3 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2"
        >
          <CreditCard className="w-5 h-5" />
          Gérer mes crédits
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CreditsSettings;
